import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useDepartments } from "@/contexts/DepartmentsContext";
import PersonFormDialog from "@/components/PersonFormDialog";
import {
  Upload,
  Camera,
  Loader2,
  CheckCircle2,
  X,
  Save,
  ArrowLeft,
  UserPlus,
} from "lucide-react";

/**
 * WalkaroundImport — Snap-to-Import for the paper walk-around sheet.
 *
 * Workflow:
 *  1. Manager tap "Choose photo" (or opens camera on iPad) and picks a shot
 *     of the filled-in sheet.
 *  2. POST /api/assets/walkaround-parse → Gemini vision returns parsed rows +
 *     the top header (Location / Walker / Date).
 *  3. All rows land in an editable table. Manager can tick/untick per row,
 *     patch values, apply the top-level location as a default.
 *  4. "Import ticked rows" → POST /api/assets/bulk. For rows with qty>1 the
 *     backend expands to N records with (i/N) suffix in the name.
 *  5. Redirect to /assets with a toast summarising what landed.
 *
 * Design constraint: NOTHING lands in the register until the manager clicks
 * the final import button — Gemini transcription can misread, we can't
 * silently create N garbage rows.
 */
const CONDITIONS = ["", "new", "good", "fair", "worn", "broken"];
// Statuses the manager can pick per row in the review table. Matches the
// backend ALLOWED_STATUSES on /assets/bulk. Default in the row-header
// dropdown is "in_use" because the manager confirmed most walk-around
// imports are gear that's already deployed (desks, monitors, laptops).
const STATUSES = [
  { v: "in_use", label: "In use" },
  { v: "available", label: "Available" },
  { v: "broken_unconfirmed", label: "Broken (unconfirmed)" },
  { v: "broken", label: "Broken" },
  { v: "retired", label: "Retired" },
  { v: "lost", label: "Lost" },
];

/**
 * smartCase — Gemini often echoes the source verbatim, so a sheet with
 * "DELL LATITUDE 5540" comes back in caps. Convert fully-uppercase
 * strings to Title Case, but leave anything with lowercase letters
 * alone so intentional casing ("HP", "iPad", "3M", "iPhone") survives.
 */
const smartCase = (s) => {
  if (!s || typeof s !== "string") return s;
  if (/[a-z]/.test(s)) return s;
  return s
    .toLowerCase()
    .replace(/(^|[\s-/])(\w)/g, (_, sep, ch) => sep + ch.toUpperCase());
};

const WalkaroundImport = () => {
  const { departments } = useDepartments();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [headerLocation, setHeaderLocation] = useState("");
  const [headerWalker, setHeaderWalker] = useState("");
  const [headerDate, setHeaderDate] = useState("");
  const [defaultDept, setDefaultDept] = useState("");
  const [defaultStatus, setDefaultStatus] = useState("in_use");
  const [defaultAssignee, setDefaultAssignee] = useState("");
  const [rows, setRows] = useState([]); // {picked, category, name, qty, brand, model, serial, condition, notes, status, assignee_text}
  const [categories, setCategories] = useState([]);
  const [preview, setPreview] = useState(null);
  // v188 — load People + Users so the assignee dropdown can persist a
  // real link instead of drifting into free-text (which historically
  // rendered as "Unassigned" if the backend never set assignee_kind).
  const [people, setPeople] = useState([]);
  const [users, setUsers] = useState([]);
  const [personDlgOpen, setPersonDlgOpen] = useState(false);
  const [personDlgSeed, setPersonDlgSeed] = useState("");
  const [personDlgOnSaved, setPersonDlgOnSaved] = useState(null);

  useEffect(() => {
    api
      .get("/asset-categories")
      .then(({ data }) => setCategories(data || []))
      .catch(() => {});
    api
      .get("/people")
      .then(({ data }) => setPeople(data || []))
      .catch(() => {});
    api
      .get("/users/public")
      .then(({ data }) => setUsers(data || []))
      .catch(() => {});
  }, []);

  const catNames = useMemo(
    () => (categories || []).map((c) => c.name),
    [categories],
  );

  const handleFile = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    setRows([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/assets/walkaround-parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaderLocation(smartCase(data.location || ""));
      setHeaderWalker(smartCase(data.walker || ""));
      setHeaderDate(data.date || "");
      const parsed = (data.rows || []).map((r) => ({
        picked: true,
        category: smartCase(r.category || ""),
        name: smartCase(r.name || ""),
        qty: Number(r.qty) || 1,
        brand: smartCase(r.brand || ""),
        model: smartCase(r.model || ""),
        // Serials are alphanumeric codes — keep them verbatim (mixed case
        // matters). Same for notes/assignee where the source hand-written
        // note might legitimately be in caps for emphasis.
        serial: r.serial || "",
        condition: (r.condition || "").toLowerCase(),
        notes: smartCase(r.notes || ""),
        status: "",
        // v188 — Row-level assignee value now encodes kind:id, e.g.
        // "person:8936f7bb-…", "user:2b85c…", "text:Lindy Field" or "".
        // Empty means "use the default_assignee" from the row-header.
        assignee_value: "",
        // Kept for OCR echoes of hand-written names (Gemini surfaces them
        // as text). Persisted as `text:…` below if the manager doesn't
        // pick a real Person/User.
        assignee_text: smartCase(r.assignee || ""),
      }));
      setRows(parsed);
      toast.success(`Read ${parsed.length} row${parsed.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the photo");
    } finally {
      setBusy(false);
    }
  };

  const patch = (i, k, v) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const importAll = async () => {
    const picked = rows.filter((r) => r.picked && r.name.trim());
    if (!picked.length) return toast.error("Tick at least one row to import");
    setImporting(true);
    try {
      const body = {
        rows: picked.map((r) => {
          // v188 — resolve the row's assignee (falling back to
          // defaultAssignee) into proper kind/id/text triple so the
          // backend doesn't drop it into "Unassigned" like it used to.
          const raw = r.assignee_value || defaultAssignee || "";
          let a_kind = null, a_id = null, a_text = null;
          if (raw.startsWith("person:")) {
            a_kind = "person";
            a_id = raw.slice(7);
          } else if (raw.startsWith("user:")) {
            a_kind = "user";
            a_id = raw.slice(5);
          } else if (raw.startsWith("text:")) {
            a_kind = "text";
            a_text = raw.slice(5) || null;
          } else if (r.assignee_text) {
            // OCR-echoed hand-written name with no explicit pick — save
            // as text so the manager can promote to a Person later.
            a_kind = "text";
            a_text = r.assignee_text;
          }
          return {
            ...r,
            location: headerLocation || null,
            department: defaultDept || null,
            notes: r.notes || null,
            // Row-level values win over the row-header defaults so a
            // manager can flip one broken laptop to "Broken" even if the
            // whole shipment defaults to "In use".
            status: r.status || defaultStatus || "available",
            assignee_kind: a_kind,
            assignee_id: a_id,
            assignee_text: a_text,
          };
        }),
      };
      const { data } = await api.post("/assets/bulk", body);
      toast.success(
        `Imported ${data.created_count} asset record${data.created_count === 1 ? "" : "s"}` +
          (data.error_count ? ` — ${data.error_count} skipped` : ""),
      );
      nav("/assets");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setImporting(false);
    }
  };

  const totalUnits = rows
    .filter((r) => r.picked)
    .reduce((s, r) => s + (Number(r.qty) || 1), 0);

  return (
    <div className="p-5 pb-24" data-testid="walkaround-import-page">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => nav("/assets")} className="nb-btn bg-white p-2" aria-label="back">
          <ArrowLeft size={16} strokeWidth={2.5} />
        </button>
        <div>
          <div className="label-eyebrow">Assets · Snap-to-Import</div>
          <h1 className="font-display text-3xl">Import a walk-around photo</h1>
          <p className="text-sm text-[#607087] mt-1">
            Photograph a filled walk-around sheet, review what the app read, then
            commit the good rows into the register in one hit.
          </p>
        </div>
      </div>

      {rows.length === 0 && (
        <div
          className="nb-card p-8 text-center max-w-2xl mx-auto"
          data-testid="walkaround-upload-panel"
        >
          {preview ? (
            <img
              src={preview}
              alt="upload preview"
              className="max-h-80 mx-auto rounded-lg border-2 border-black mb-4"
            />
          ) : (
            <div className="text-[#607087] mb-4">
              Landscape shots work best. Make sure the row lines are visible and
              the writing is roughly in focus.
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-center">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="walkaround-camera-input"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="walkaround-file-input"
            />
            <button
              data-testid="walkaround-camera-btn"
              onClick={() => cameraRef.current?.click()}
              disabled={busy}
              className="nb-btn nb-btn-primary px-4 py-3 flex items-center gap-2"
            >
              <Camera size={16} strokeWidth={2.5} />
              {busy ? "Reading photo…" : "Take photo"}
            </button>
            <button
              data-testid="walkaround-choose-btn"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="nb-btn bg-white px-4 py-3 flex items-center gap-2"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" strokeWidth={2.5} />
              ) : (
                <Upload size={16} strokeWidth={2.5} />
              )}
              {busy ? "Reading photo…" : "Choose photo"}
            </button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="nb-card p-4 mb-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div>
              <div className="label-eyebrow mb-1">Location (applies to all rows)</div>
              <input
                data-testid="walkaround-location-input"
                className="nb-input"
                value={headerLocation}
                onChange={(e) => setHeaderLocation(e.target.value)}
                placeholder="e.g. Boardroom"
              />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Default department</div>
              <select
                data-testid="walkaround-dept-input"
                className="nb-input"
                value={defaultDept}
                onChange={(e) => setDefaultDept(e.target.value)}
              >
                <option value="">— none —</option>
                {(departments || []).map((d) => (
                  <option key={d.id} value={d.name}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Default status</div>
              <select
                data-testid="walkaround-default-status"
                className="nb-input"
                value={defaultStatus}
                onChange={(e) => setDefaultStatus(e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s.v} value={s.v}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Default assignee</div>
              {/* v188 — Was a free-text input that never saved because
                  the backend hardcoded `assignee_kind: None`. Now a
                  picker of People + Users + "type-a-name" fallback with
                  inline "+ Add person" so managers can capture new staff
                  on the fly and every row ends up correctly linked. */}
              <AssigneePicker
                data-testid="walkaround-default-assignee"
                value={defaultAssignee}
                onChange={setDefaultAssignee}
                people={people}
                users={users}
                onAddPerson={(seed, cb) => {
                  setPersonDlgSeed(seed || "");
                  setPersonDlgOnSaved(() => cb);
                  setPersonDlgOpen(true);
                }}
                placeholder="Pick or type a name"
              />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Walker</div>
              <input
                className="nb-input"
                value={headerWalker}
                onChange={(e) => setHeaderWalker(e.target.value)}
                placeholder="Name (for reference)"
              />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Date</div>
              <input
                className="nb-input"
                value={headerDate}
                onChange={(e) => setHeaderDate(e.target.value)}
                placeholder="dd / mm / yyyy"
              />
            </div>
          </div>

          <div className="nb-card p-0 overflow-x-auto mb-4">
            <table className="min-w-[1400px] w-full text-sm">
              <thead className="bg-[#F4F8FB] border-b-2 border-black">
                <tr className="text-left">
                  <th className="p-3 w-10"></th>
                  <th className="p-3 label-eyebrow w-[12%]">Category</th>
                  <th className="p-3 label-eyebrow w-[18%]">Name</th>
                  <th className="p-3 label-eyebrow w-[5%]">Qty</th>
                  <th className="p-3 label-eyebrow w-[9%]">Brand</th>
                  <th className="p-3 label-eyebrow w-[9%]">Model</th>
                  <th className="p-3 label-eyebrow w-[9%]">Serial</th>
                  <th className="p-3 label-eyebrow w-[7%]">Cond</th>
                  <th className="p-3 label-eyebrow w-[9%]">Status</th>
                  <th className="p-3 label-eyebrow w-[11%]">Assigned to</th>
                  <th className="p-3 label-eyebrow w-[11%]">Notes</th>
                  <th className="p-3 label-eyebrow w-[5%]"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    data-testid={`walkaround-row-${i}`}
                    className={`border-b border-black/10 ${!r.picked ? "opacity-40" : ""}`}
                  >
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        data-testid={`walkaround-row-picked-${i}`}
                        checked={r.picked}
                        onChange={(e) => patch(i, "picked", e.target.checked)}
                        className="h-5 w-5"
                      />
                    </td>
                    <td className="p-2">
                      <select
                        className="qv-input"
                        value={r.category}
                        onChange={(e) => patch(i, "category", e.target.value)}
                      >
                        <option value="">— pick —</option>
                        {r.category && !catNames.includes(r.category) && (
                          <option value={r.category}>{r.category} (from photo)</option>
                        )}
                        {catNames.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <input
                        className="qv-input"
                        value={r.name}
                        onChange={(e) => patch(i, "name", e.target.value)}
                        placeholder="required"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="1"
                        max="200"
                        className="qv-input font-mono-num"
                        value={r.qty}
                        onChange={(e) => patch(i, "qty", Number(e.target.value) || 1)}
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="qv-input"
                        value={r.brand}
                        onChange={(e) => patch(i, "brand", e.target.value)}
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="qv-input"
                        value={r.model}
                        onChange={(e) => patch(i, "model", e.target.value)}
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="qv-input font-mono-num"
                        value={r.serial}
                        onChange={(e) => patch(i, "serial", e.target.value)}
                      />
                    </td>
                    <td className="p-2">
                      <select
                        className="qv-input"
                        value={r.condition}
                        onChange={(e) => patch(i, "condition", e.target.value)}
                      >
                        {CONDITIONS.map((c) => (
                          <option key={c} value={c}>{c || "—"}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <select
                        data-testid={`walkaround-row-status-${i}`}
                        className="qv-input"
                        value={r.status}
                        onChange={(e) => patch(i, "status", e.target.value)}
                      >
                        <option value="">
                          — use default ({STATUSES.find((s) => s.v === defaultStatus)?.label}) —
                        </option>
                        {STATUSES.map((s) => (
                          <option key={s.v} value={s.v}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <AssigneePicker
                        data-testid={`walkaround-row-assignee-${i}`}
                        value={r.assignee_value}
                        onChange={(v) => patch(i, "assignee_value", v)}
                        people={people}
                        users={users}
                        // v188 — Seed the "+ Add person" dialog with any
                        // OCR-echoed hand-written name Gemini pulled off
                        // the paper sheet, so tapping the button opens
                        // pre-filled with "Lindy" etc.
                        onAddPerson={(seed, cb) => {
                          setPersonDlgSeed(seed || r.assignee_text || "");
                          setPersonDlgOnSaved(() => cb);
                          setPersonDlgOpen(true);
                        }}
                        placeholder={
                          defaultAssignee
                            ? `default: ${labelFromValue(defaultAssignee, people, users)}`
                            : r.assignee_text || ""
                        }
                        inline
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="qv-input"
                        value={r.notes}
                        onChange={(e) => patch(i, "notes", e.target.value)}
                      />
                    </td>
                    <td className="p-2 text-right">
                      <button
                        data-testid={`walkaround-row-remove-${i}`}
                        onClick={() => removeRow(i)}
                        className="nb-btn bg-white p-1.5"
                        aria-label="remove"
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sticky bottom-4 nb-card p-4 flex items-center justify-between shadow-lg">
            <div className="text-sm">
              <b>{rows.filter((r) => r.picked).length}</b> row
              {rows.filter((r) => r.picked).length === 1 ? "" : "s"} ticked ·{" "}
              will create <b>{totalUnits}</b> asset record
              {totalUnits === 1 ? "" : "s"} (Qty expanded)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => nav("/assets")}
                className="nb-btn bg-white px-4 py-2 flex items-center gap-1"
              >
                Cancel
              </button>
              <button
                data-testid="walkaround-import-btn"
                onClick={importAll}
                disabled={importing || totalUnits === 0}
                className="nb-btn nb-btn-primary px-5 py-2 flex items-center gap-2"
              >
                {importing ? (
                  <Loader2 size={14} className="animate-spin" strokeWidth={2.5} />
                ) : (
                  <CheckCircle2 size={14} strokeWidth={2.5} />
                )}
                {importing ? "Importing…" : `Import ${totalUnits}`}
              </button>
            </div>
          </div>
        </>
      )}

      {/* v188 — Inline "+ Add person" dialog for the assignee picker.
          Auto-selects the newly-created person via the onSaved callback
          the picker registered. */}
      <PersonFormDialog
        open={personDlgOpen}
        person={personDlgSeed ? { name: personDlgSeed } : null}
        onClose={() => setPersonDlgOpen(false)}
        onSaved={async () => {
          const { data } = await api.get("/people").catch(() => ({ data: [] }));
          setPeople(data || []);
          if (personDlgOnSaved && data?.length) {
            const newest = [...data].sort((a, b) =>
              (b.created_at || "").localeCompare(a.created_at || ""),
            )[0];
            personDlgOnSaved(newest);
          }
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// AssigneePicker — searchable dropdown of active People + login Users with
// an inline "+ Add new person" that opens the PersonFormDialog pre-seeded
// with the manager's typed text. Emits kind-prefixed values so the caller
// (row) knows whether the pick was a person, user, or free-text fallback.
// ---------------------------------------------------------------------------
const labelFromValue = (val, people, users) => {
  if (!val) return "";
  if (val.startsWith("person:")) {
    const p = (people || []).find((x) => x.id === val.slice(7));
    return p ? p.name : "(missing)";
  }
  if (val.startsWith("user:")) {
    const u = (users || []).find((x) => x.id === val.slice(5));
    return u ? u.name : "(missing)";
  }
  if (val.startsWith("text:")) return val.slice(5);
  return val;
};

const AssigneePicker = ({
  value,
  onChange,
  people,
  users,
  onAddPerson,
  placeholder,
  inline,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const btnRef = useRef(null);
  // v189 — Portal the dropdown to document.body with fixed positioning
  // anchored to the button's rect. Fixes mobile bug where the menu was
  // absolute-positioned inside a horizontally-scrolling table cell and
  // got clipped by the next section (overflow-x-auto + narrow viewport).
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        !document.getElementById("assignee-picker-menu")?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = labelFromValue(value, people, users);
  const query = q.trim().toLowerCase();
  const options = useMemo(() => {
    const p = (people || [])
      .filter((x) => x.active !== false)
      .filter((x) => !query || (x.name || "").toLowerCase().includes(query))
      .map((x) => ({
        value: `person:${x.id}`,
        label: x.name,
        sub: [x.role, x.department].filter(Boolean).join(" · "),
        badge: "Person",
      }));
    const u = (users || [])
      .filter((x) => !query || (x.name || "").toLowerCase().includes(query))
      .map((x) => ({
        value: `user:${x.id}`,
        label: x.name,
        sub: x.role || "",
        badge: "Login",
      }));
    return [...p, ...u].sort((a, b) => a.label.localeCompare(b.label));
  }, [people, users, query]);

  const commitFreeText = () => {
    const v = q.trim();
    if (!v) return;
    onChange(`text:${v}`);
    setQ("");
    setOpen(false);
  };

  // Compute a menu width/max-height that works on tight mobile
  // viewports (390px). Use viewport width fallbacks so narrow columns
  // don't produce a pinhole dropdown.
  const menuStyle = rect
    ? (() => {
        const vw = typeof window !== "undefined" ? window.innerWidth : 800;
        const vh = typeof window !== "undefined" ? window.innerHeight : 800;
        const width = Math.min(Math.max(rect.width, 240), vw - 16);
        const left = Math.max(8, Math.min(rect.left, vw - width - 8));
        const maxH = Math.min(320, vh - rect.top - 16);
        return {
          position: "fixed",
          top: rect.top + 4,
          left,
          width,
          maxHeight: maxH,
        };
      })()
    : { display: "none" };

  return (
    <div className="relative" data-testid={rest["data-testid"]}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${inline ? "qv-input" : "nb-input"} text-left w-full flex items-center justify-between gap-2`}
      >
        <span className={label ? "truncate" : "text-[#607087] truncate"}>
          {label || placeholder || "Pick…"}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            aria-label="clear"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            className="text-[#607087] hover:text-black px-1"
          >
            ×
          </span>
        )}
      </button>
      {open && typeof document !== "undefined" &&
        createPortal(
          <div
            id="assignee-picker-menu"
            className="nb-card p-2 bg-white shadow-xl overflow-auto z-[9999]"
            style={menuStyle}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <input
              ref={inputRef}
              autoFocus
              className="nb-input py-1.5 text-sm mb-2"
              placeholder="Search or type a name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && options.length === 0) commitFreeText();
              }}
            />
            {options.length === 0 ? (
              <div className="text-xs text-[#607087] p-2">
                No match.
                {q.trim() && (
                  <div className="mt-2 flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={commitFreeText}
                      className="nb-btn bg-white px-2 py-1 text-xs"
                    >
                      Use "{q.trim()}" as free text
                    </button>
                    {onAddPerson && (
                      <button
                        type="button"
                        onClick={() =>
                          onAddPerson(q.trim(), (created) => {
                            if (created?.id) onChange(`person:${created.id}`);
                            setOpen(false);
                            setQ("");
                          })
                        }
                        className="nb-btn bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A] px-2 py-1 text-xs flex items-center gap-1"
                      >
                        <UserPlus size={10} strokeWidth={2.5} /> Add "{q.trim()}" as new person
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setQ("");
                  }}
                  className={`w-full text-left px-2 py-1.5 rounded hover:bg-[#F0F7FB] text-sm ${
                    value === o.value ? "bg-[#DFEEF7]" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{o.label}</span>
                    <span
                      className={`text-[9px] font-bold tracking-wider uppercase px-1 rounded ${
                        o.badge === "Person"
                          ? "bg-[#DFEEF7] text-[#0A3A7A]"
                          : "bg-[#FFF6DF] text-[#7A5300]"
                      }`}
                    >
                      {o.badge}
                    </span>
                  </div>
                  {o.sub && <div className="text-[10px] text-[#607087]">{o.sub}</div>}
                </button>
              ))
            )}
            {onAddPerson && options.length > 0 && q.trim() && (
              <button
                type="button"
                onClick={() =>
                  onAddPerson(q.trim(), (created) => {
                    if (created?.id) onChange(`person:${created.id}`);
                    setOpen(false);
                    setQ("");
                  })
                }
                className="w-full text-left mt-1 px-2 py-1.5 rounded text-xs text-[#0A3A7A] hover:bg-[#F0F7FB] flex items-center gap-1"
              >
                <UserPlus size={12} strokeWidth={2.5} /> Add "{q.trim()}" as new person
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default WalkaroundImport;
